import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { getPortfolioCompanies } from "../mock";
import { chatCompletion } from "../services/ai";
import { Plus, Trash2, Sparkles, TrendingUp, Target, MessageSquare, BarChart3, Building2 } from "lucide-react";

// Hook to listen for team changes
function useTeamChange() {
  const [teamKey, setTeamKey] = useState(0);
  useEffect(() => {
    const handler = () => setTeamKey(k => k + 1);
    window.addEventListener("atlas_team_changed", handler);
    return () => window.removeEventListener("atlas_team_changed", handler);
  }, []);
  return teamKey;
}

// ─── Storage helpers ───────────────────────────────────────────────────────────
const store = (key, val) => localStorage.setItem(key, JSON.stringify(val));
const load = (key, def = []) => { try { return JSON.parse(localStorage.getItem(key) || JSON.stringify(def)); } catch { return def; } };

const KPIS_KEY = "atlas_portfolio_kpis";
const UPDATES_KEY = "atlas_portfolio_updates";
const VC_KEY = "atlas_value_creation";

// ─── Constants ────────────────────────────────────────────────────────────────
const KPI_STATUSES = ["On Track", "At Risk", "Off Track", "Exceeded"];
const VC_CATEGORIES = ["Revenue", "Margin", "Capital Efficiency", "Governance", "M&A", "Technology"];
const VC_STATUSES = ["Planned", "In Progress", "Complete", "Paused"];
const UPDATE_TYPES = ["Operating", "Financial", "Strategic", "Risk", "Exit Prep"];

const KPI_STATUS_COLORS = {
  "On Track": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "At Risk": "bg-yellow-50 text-yellow-700 border-yellow-200",
  "Off Track": "bg-red-50 text-red-700 border-red-200",
  "Exceeded": "bg-blue-50 text-blue-700 border-blue-200",
};
const VC_STATUS_COLORS = {
  Planned: "bg-gray-100 text-gray-600 border-gray-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  Complete: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Paused: "bg-yellow-50 text-yellow-700 border-yellow-200",
};
const VC_CAT_COLORS = {
  Revenue: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Margin: "bg-blue-50 text-blue-700 border-blue-200",
  "Capital Efficiency": "bg-purple-50 text-purple-700 border-purple-200",
  Governance: "bg-gray-100 text-gray-600 border-gray-200",
  "M&A": "bg-orange-50 text-orange-700 border-orange-200",
  Technology: "bg-cyan-50 text-cyan-700 border-cyan-200",
};

export default function PortfolioOps() {
  const teamKey = useTeamChange();
  const companies = useMemo(() => getPortfolioCompanies(), [teamKey]);
  const [selectedCompany, setSelectedCompany] = useState(companies[0] || null);
  const [companySearch, setCompanySearch] = useState("");
  const [activeTab, setActiveTab] = useState("companies");

  // Exit Strategy Dialog
  const [exitOpen, setExitOpen] = useState(false);
  const [exitSel, setExitSel] = useState(null);
  const [exitBusy, setExitBusy] = useState(false);
  const [exitStrategy, setExitStrategy] = useState("");

  // Update selectedCompany when team changes
  useEffect(() => {
    if (companies.length > 0 && !companies.find(c => c.id === selectedCompany?.id)) {
      setSelectedCompany(companies[0]);
    }
  }, [companies, selectedCompany?.id]);

  const openExitStrategy = async (p) => {
    setExitSel(p); setExitOpen(true); setExitBusy(true); setExitStrategy("");
    try {
      const message = `Portfolio company: ${p.name}\nSector: ${p.sector || "N/A"}\nCountry: ${p.country || "N/A"}\nIRR: ${p.irr}%\nMOIC: ${p.moic}x\nRevenue: $${p.revenue}M\nEBITDA: $${p.ebitda}M\nValue Drivers: ${(p.valueDrivers || []).join(", ")}\nExit Readiness: ${p.exitReadiness}%\n\nRecommend: 1) Best exit path with rationale, 2) Timeline and milestones, 3) Value-maximizing levers in next 6-12 months.`;
      const { content } = await chatCompletion(message, { systemPrompt: "You are a seasoned PE exit advisor at GII. Be concise, structured, and action-oriented." });
      setExitStrategy(content || "");
    } catch (e) { setExitStrategy(`Error: ${e.message}`); } finally { setExitBusy(false); }
  };

  // KPIs
  const [kpis, setKpis] = useState(() => load(KPIS_KEY, []));
  const [newKpi, setNewKpi] = useState({ metric: "", value: "", unit: "", period: "", target: "", status: "On Track" });

  // Updates
  const [updates, setUpdates] = useState(() => load(UPDATES_KEY, []));
  const [newUpdate, setNewUpdate] = useState({ update_type: "Operating", content: "", author: "" });

  // Value Creation
  const [initiatives, setInitiatives] = useState(() => load(VC_KEY, []));
  const [newInit, setNewInit] = useState({ title: "", description: "", category: "Revenue", owner: "", target_impact: "", due_date: "" });

  // AI
  const [aiBusy, setAiBusy] = useState(false);
  const [aiOutput, setAiOutput] = useState("");

  useEffect(() => { store(KPIS_KEY, kpis); }, [kpis]);
  useEffect(() => { store(UPDATES_KEY, updates); }, [updates]);
  useEffect(() => { store(VC_KEY, initiatives); }, [initiatives]);

  const cid = selectedCompany?.id || "";
  const companyKpis = kpis.filter(k => k.company_id === cid);
  const companyUpdates = updates.filter(u => u.company_id === cid);
  const companyInits = initiatives.filter(i => i.company_id === cid);

  const filteredCompanies = useMemo(() => {
    const q = companySearch.toLowerCase();
    return companies.filter(c => !q || c.name.toLowerCase().includes(q));
  }, [companies, companySearch]);

  // KPI helpers
  const addKpi = () => {
    if (!newKpi.metric.trim() || !cid) return;
    const obj = { id: crypto.randomUUID(), company_id: cid, ...newKpi, value: parseFloat(newKpi.value) || null, target: parseFloat(newKpi.target) || null, created_at: Date.now() };
    setKpis(prev => [...prev, obj]);
    setNewKpi({ metric: "", value: "", unit: "", period: "", target: "", status: "On Track" });
  };
  const updateKpiStatus = (id, status) => setKpis(prev => prev.map(k => k.id === id ? { ...k, status } : k));
  const deleteKpi = (id) => setKpis(prev => prev.filter(k => k.id !== id));

  // Update helpers
  const addUpdate = () => {
    if (!newUpdate.content.trim() || !cid) return;
    const obj = { id: crypto.randomUUID(), company_id: cid, ...newUpdate, created_at: Date.now() };
    setUpdates(prev => [obj, ...prev]);
    setNewUpdate(p => ({ ...p, content: "" }));
  };
  const deleteUpdate = (id) => setUpdates(prev => prev.filter(u => u.id !== id));

  // Initiative helpers
  const addInit = () => {
    if (!newInit.title.trim() || !cid) return;
    const obj = { id: crypto.randomUUID(), company_id: cid, ...newInit, status: "Planned", created_at: Date.now(), updated_at: Date.now() };
    setInitiatives(prev => [obj, ...prev]);
    setNewInit({ title: "", description: "", category: "Revenue", owner: "", target_impact: "", due_date: "" });
  };
  const updateInitStatus = (id, status) => setInitiatives(prev => prev.map(i => i.id === id ? { ...i, status, updated_at: Date.now() } : i));
  const deleteInit = (id) => setInitiatives(prev => prev.filter(i => i.id !== id));

  // AI insights
  const runAI = async () => {
    if (!selectedCompany) return;
    setAiBusy(true);
    setAiOutput("");
    try {
      const prompt = `Portfolio company: ${selectedCompany.name} (sector: ${selectedCompany.sector}). IRR: ${selectedCompany.irr}%, MOIC: ${selectedCompany.moic}x, Revenue: $${selectedCompany.revenue}M, EBITDA: $${selectedCompany.ebitda}M, Exit Readiness: ${selectedCompany.exitReadiness}/100. Value Drivers: ${(selectedCompany.valueDrivers || []).join(", ")}. Provide 4 actionable portfolio management insights: 1) Near-term operational priorities, 2) Value creation opportunities with rough EBITDA impact, 3) Risk flags to monitor, 4) Exit readiness assessment with specific recommendations.`;
      const { content } = await chatCompletion(prompt, { systemPrompt: "You are a PE operating partner. Be specific, quantitative, and actionable." });
      setAiOutput(content || "");
    } catch (e) {
      setAiOutput(`Error: ${e.message}`);
    } finally {
      setAiBusy(false);
    }
  };

  // VC progress
  const vcTotal = companyInits.length;
  const vcComplete = companyInits.filter(i => i.status === "Complete").length;
  const vcPct = vcTotal > 0 ? Math.round((vcComplete / vcTotal) * 100) : 0;

  return (
    <div className="space-y-6 p-6 bg-white min-h-screen">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-atlas-heading">Portfolio</h1>
        <p className="text-sm text-atlas-description mt-1">Portfolio companies, KPI tracking, value creation initiatives, and updates</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 border border-gray-200">
          <TabsTrigger value="companies" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
            <Building2 size={14} className="mr-1" /> Companies
          </TabsTrigger>
          <TabsTrigger value="operations" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
            <BarChart3 size={14} className="mr-1" /> Operations
          </TabsTrigger>
        </TabsList>

        {/* Companies Tab */}
        <TabsContent value="companies" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map(p => (
              <Card key={p.id} onClick={() => openExitStrategy(p)} className="border border-gray-100 shadow-sm cursor-pointer hover:border-atlas-accent hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-bold text-sm text-atlas-heading">{p.name}</div>
                      <div className="text-xs text-atlas-description mt-0.5">{p.sector} • {p.country}</div>
                    </div>
                    <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{p.moic}x</span>
                  </div>
                  <div className="text-xs text-atlas-description mb-2">IRR {p.irr}% • Rev ${p.revenue}M • EBITDA ${p.ebitda}M</div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-atlas-description">Exit Readiness</span>
                      <span className="text-xs font-semibold text-atlas-heading">{p.exitReadiness}%</span>
                    </div>
                    <Progress value={p.exitReadiness} className="h-1.5" />
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(p.valueDrivers || []).map(v => (
                      <span key={v} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-atlas-accent">{v}</span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Exit Strategy Dialog */}
          <Dialog open={exitOpen} onOpenChange={setExitOpen}>
            <DialogContent className="bg-white text-atlas-heading border-gray-200 max-w-2xl">
              <DialogHeader><DialogTitle className="text-atlas-heading">Exit Strategy • {exitSel?.name || ""}</DialogTitle></DialogHeader>
              <Card className="border border-gray-100">
                <CardContent className="p-4 text-sm whitespace-pre-wrap min-h-40 text-atlas-heading">
                  {exitBusy ? "Generating exit strategy…" : (exitStrategy || "Click a portfolio card to generate strategy.")}
                </CardContent>
              </Card>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="mt-4">
          <div className="grid lg:grid-cols-4 gap-6">
        {/* Company Selector */}
        <div className="lg:col-span-1">
          <Card className="border border-gray-100 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-atlas-heading">Portfolio Companies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input
                placeholder="Search companies…"
                value={companySearch}
                onChange={e => setCompanySearch(e.target.value)}
                className="text-sm border-gray-200"
              />
              <div className="max-h-80 overflow-y-auto space-y-1">
                {filteredCompanies.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setSelectedCompany(c)}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${selectedCompany?.id === c.id ? "bg-atlas-accent text-[#383838]" : "hover:bg-gray-50 text-atlas-heading"}`}
                  >
                    <div className="font-medium truncate">{c.name}</div>
                    <div className={`text-xs ${selectedCompany?.id === c.id ? "text-blue-100" : "text-atlas-description"}`}>
                      {c.sector} • {c.moic}x MOIC
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-4">
          {!selectedCompany ? (
            <Card className="border border-gray-100 shadow-sm">
              <CardContent className="p-8 text-center text-atlas-description">Select a portfolio company from the left to begin.</CardContent>
            </Card>
          ) : (
            <>
              {/* Company Header */}
              <Card className="border border-gray-100 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-atlas-heading">{selectedCompany.name}</h2>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <Badge className="text-xs bg-gray-100 text-gray-600 border-gray-200">{selectedCompany.sector}</Badge>
                        <span className="text-xs text-atlas-description">IRR: <strong className="text-atlas-heading">{selectedCompany.irr}%</strong></span>
                        <span className="text-xs text-atlas-description">MOIC: <strong className="text-atlas-heading">{selectedCompany.moic}x</strong></span>
                        <span className="text-xs text-atlas-description">Revenue: <strong className="text-atlas-heading">${selectedCompany.revenue}M</strong></span>
                        <span className="text-xs text-atlas-description">EBITDA: <strong className="text-atlas-heading">${selectedCompany.ebitda}M</strong></span>
                      </div>
                    </div>
                    <Button onClick={runAI} disabled={aiBusy} className="bg-atlas-accent hover:bg-blue-700 text-[#383838] text-sm">
                      <Sparkles size={14} className="mr-1" />{aiBusy ? "Analyzing…" : "AI Portfolio Insights"}
                    </Button>
                  </div>
                  {/* Exit Readiness */}
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs text-atlas-description mb-1">
                      <span>Exit Readiness Score</span>
                      <span className="font-semibold text-atlas-heading">{selectedCompany.exitReadiness}/100</span>
                    </div>
                    <Progress value={selectedCompany.exitReadiness} className="h-2" />
                  </div>
                </CardContent>
              </Card>

              {/* AI Output */}
              {aiOutput && (
                <Card className="border border-blue-100 bg-blue-50 shadow-sm">
                  <CardContent className="p-4">
                    <div className="text-xs font-semibold text-atlas-accent mb-2 uppercase tracking-wide">AI Portfolio Insights</div>
                    <div className="text-sm text-atlas-heading whitespace-pre-wrap">{aiOutput}</div>
                  </CardContent>
                </Card>
              )}

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="bg-gray-100 border border-gray-200">
                  <TabsTrigger value="kpis" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
                    <BarChart3 size={14} className="mr-1" /> KPIs
                  </TabsTrigger>
                  <TabsTrigger value="value-creation" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
                    <Target size={14} className="mr-1" /> Value Creation
                  </TabsTrigger>
                  <TabsTrigger value="updates" className="text-sm data-[state=active]:bg-atlas-accent data-[state=active]:text-white">
                    <MessageSquare size={14} className="mr-1" /> Updates
                  </TabsTrigger>
                </TabsList>

                {/* KPIs Tab */}
                <TabsContent value="kpis" className="space-y-4 mt-4">
                  <Card className="border border-gray-100 shadow-sm">
                    <CardContent className="p-3 space-y-2">
                      <div className="grid md:grid-cols-4 gap-2">
                        <Input placeholder="Metric (e.g. ARR)" value={newKpi.metric} onChange={e => setNewKpi(p => ({ ...p, metric: e.target.value }))} className="text-sm border-gray-200" />
                        <Input placeholder="Value" type="number" value={newKpi.value} onChange={e => setNewKpi(p => ({ ...p, value: e.target.value }))} className="text-sm border-gray-200" />
                        <Input placeholder="Target" type="number" value={newKpi.target} onChange={e => setNewKpi(p => ({ ...p, target: e.target.value }))} className="text-sm border-gray-200" />
                        <Input placeholder="Unit (e.g. $M, %)" value={newKpi.unit} onChange={e => setNewKpi(p => ({ ...p, unit: e.target.value }))} className="text-sm border-gray-200" />
                        <Input placeholder="Period (e.g. Q1 2025)" value={newKpi.period} onChange={e => setNewKpi(p => ({ ...p, period: e.target.value }))} className="text-sm border-gray-200" />
                        <select value={newKpi.status} onChange={e => setNewKpi(p => ({ ...p, status: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                          {KPI_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <Button onClick={addKpi} className="bg-atlas-accent hover:bg-blue-700 text-[#383838] text-sm">
                        <Plus size={14} className="mr-1" /> Add KPI
                      </Button>
                    </CardContent>
                  </Card>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-xs text-atlas-description">Metric</TableHead>
                          <TableHead className="text-xs text-atlas-description">Value</TableHead>
                          <TableHead className="text-xs text-atlas-description">Target</TableHead>
                          <TableHead className="text-xs text-atlas-description">Period</TableHead>
                          <TableHead className="text-xs text-atlas-description">Status</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {companyKpis.length === 0 && (
                          <TableRow><TableCell colSpan={6} className="text-center text-atlas-description text-sm py-6">No KPIs tracked yet.</TableCell></TableRow>
                        )}
                        {companyKpis.map(k => {
                          const pct = k.value != null && k.target != null && k.target > 0 ? Math.min(100, Math.round((k.value / k.target) * 100)) : null;
                          return (
                            <TableRow key={k.id} className="hover:bg-gray-50">
                              <TableCell className="text-sm font-medium text-atlas-heading">{k.metric}</TableCell>
                              <TableCell className="text-sm text-atlas-heading">{k.value != null ? `${k.value}${k.unit ? " " + k.unit : ""}` : "—"}</TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <div className="text-sm text-atlas-description">{k.target != null ? `${k.target}${k.unit ? " " + k.unit : ""}` : "—"}</div>
                                  {pct != null && <Progress value={pct} className="h-1.5 w-20" />}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-atlas-description">{k.period || "—"}</TableCell>
                              <TableCell>
                                <select value={k.status} onChange={e => updateKpiStatus(k.id, e.target.value)} className={`text-xs border rounded px-2 py-1 ${KPI_STATUS_COLORS[k.status]}`}>
                                  {KPI_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </TableCell>
                              <TableCell>
                                <button onClick={() => deleteKpi(k.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                {/* Value Creation Tab */}
                <TabsContent value="value-creation" className="space-y-4 mt-4">
                  {vcTotal > 0 && (
                    <Card className="border border-gray-100 shadow-sm">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between text-xs text-atlas-description mb-1">
                          <span>Value Creation Progress</span>
                          <span>{vcComplete}/{vcTotal} complete ({vcPct}%)</span>
                        </div>
                        <Progress value={vcPct} className="h-2" />
                      </CardContent>
                    </Card>
                  )}

                  <Card className="border border-gray-100 shadow-sm">
                    <CardContent className="p-3 space-y-2">
                      <div className="grid md:grid-cols-3 gap-2">
                        <Input placeholder="Initiative title" value={newInit.title} onChange={e => setNewInit(p => ({ ...p, title: e.target.value }))} className="text-sm border-gray-200 md:col-span-2" />
                        <select value={newInit.category} onChange={e => setNewInit(p => ({ ...p, category: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                          {VC_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <Input placeholder="Owner" value={newInit.owner} onChange={e => setNewInit(p => ({ ...p, owner: e.target.value }))} className="text-sm border-gray-200" />
                        <Input placeholder="Target impact (e.g. +$2M EBITDA)" value={newInit.target_impact} onChange={e => setNewInit(p => ({ ...p, target_impact: e.target.value }))} className="text-sm border-gray-200" />
                        <Input type="date" value={newInit.due_date} onChange={e => setNewInit(p => ({ ...p, due_date: e.target.value }))} className="text-sm border-gray-200" />
                      </div>
                      <textarea
                        placeholder="Initiative description…"
                        value={newInit.description}
                        onChange={e => setNewInit(p => ({ ...p, description: e.target.value }))}
                        rows={2}
                        className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none"
                      />
                      <Button onClick={addInit} className="bg-atlas-accent hover:bg-blue-700 text-[#383838] text-sm">
                        <Plus size={14} className="mr-1" /> Add Initiative
                      </Button>
                    </CardContent>
                  </Card>

                  <div className="space-y-2">
                    {companyInits.length === 0 && (
                      <div className="text-sm text-atlas-description text-center py-6">No value creation initiatives yet.</div>
                    )}
                    {companyInits.map(init => (
                      <div key={init.id} className="p-4 bg-white border border-gray-100 rounded-lg hover:border-gray-200 transition-colors">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-sm font-semibold text-atlas-heading">{init.title}</span>
                              <Badge className={`text-xs border ${VC_CAT_COLORS[init.category] || "bg-gray-100 text-gray-600"}`}>{init.category}</Badge>
                            </div>
                            {init.description && <div className="text-xs text-atlas-description mb-1">{init.description}</div>}
                            <div className="flex items-center gap-3 text-xs text-atlas-description flex-wrap">
                              {init.owner && <span>Owner: <strong className="text-atlas-heading">{init.owner}</strong></span>}
                              {init.target_impact && <span>Impact: <strong className="text-emerald-600">{init.target_impact}</strong></span>}
                              {init.due_date && <span>Due: <strong className="text-atlas-heading">{init.due_date}</strong></span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <select value={init.status} onChange={e => updateInitStatus(init.id, e.target.value)} className={`text-xs border rounded px-2 py-1 ${VC_STATUS_COLORS[init.status]}`}>
                              {VC_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                            <button onClick={() => deleteInit(init.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {/* Updates Tab */}
                <TabsContent value="updates" className="space-y-4 mt-4">
                  <Card className="border border-gray-100 shadow-sm">
                    <CardContent className="p-3 space-y-2">
                      <div className="grid md:grid-cols-3 gap-2">
                        <select value={newUpdate.update_type} onChange={e => setNewUpdate(p => ({ ...p, update_type: e.target.value }))} className="text-sm border border-gray-200 rounded px-2 py-2 text-atlas-heading">
                          {UPDATE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <Input placeholder="Author" value={newUpdate.author} onChange={e => setNewUpdate(p => ({ ...p, author: e.target.value }))} className="text-sm border-gray-200" />
                      </div>
                      <textarea
                        placeholder="Portfolio update content…"
                        value={newUpdate.content}
                        onChange={e => setNewUpdate(p => ({ ...p, content: e.target.value }))}
                        rows={3}
                        className="w-full text-sm border border-gray-200 rounded px-3 py-2 text-atlas-heading resize-none"
                      />
                      <Button onClick={addUpdate} className="bg-atlas-accent hover:bg-blue-700 text-[#383838] text-sm">
                        <Plus size={14} className="mr-1" /> Add Update
                      </Button>
                    </CardContent>
                  </Card>

                  <div className="space-y-3">
                    {companyUpdates.length === 0 && (
                      <div className="text-sm text-atlas-description text-center py-6">No updates yet.</div>
                    )}
                    {companyUpdates.map(u => (
                      <div key={u.id} className="p-4 bg-white border border-gray-100 rounded-lg hover:border-gray-200">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-200">{u.update_type}</Badge>
                              {u.author && <span className="text-xs text-atlas-description">by {u.author}</span>}
                              <span className="text-xs text-atlas-description">{new Date(u.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="text-sm text-atlas-heading whitespace-pre-wrap">{u.content}</div>
                          </div>
                          <button onClick={() => deleteUpdate(u.id)} className="text-red-400 hover:text-red-600 flex-shrink-0"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
